"use client";

import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

export interface ThemeContextType {
  setCurrentTheme: (theme: Theme) => void;
  theme?: Theme;
}

type Theme = string | null;

const initialContext: ThemeContextType = {
  setCurrentTheme: () => null,
  theme: undefined,
};

export function isValidTheme(string: null | string): string is Theme {
  return string ? ["dark", "light"].includes(string) : false;
}

const canUseDOM = !!(
  typeof window !== "undefined" &&
  window.document &&
  window.document.createElement
);

const ThemeContext = createContext(initialContext);

export const useTheme = () => useContext(ThemeContext) ?? initialContext;

export const ThemeProvider = ({ children }: { children: ReactNode }) => {
  const DEFAULT_THEME = "light";
  const themeLocalStorageKey = "boonen-theme";
  const getBrowserPreference = () => {
    const browserValue = window?.matchMedia?.("(prefers-color-scheme:dark)");
    return browserValue.matches ? "dark" : "light";
  };

  const [theme, setTheme] = useState<Theme | undefined>(
    canUseDOM
      ? (document.documentElement.getAttribute("data-theme") as string)
      : undefined,
  );

  const setCurrentTheme = useCallback((themeToSet: Theme | null) => {
    if (themeToSet === null) {
      window.localStorage.removeItem(themeLocalStorageKey);
      const browserPreference = getBrowserPreference();

      document.documentElement.setAttribute("data-theme", browserPreference);
      setTheme(browserPreference);
    } else {
      setTheme(themeToSet);
      window.localStorage.setItem(themeLocalStorageKey, themeToSet);
      document.documentElement.setAttribute("data-theme", themeToSet);
    }
  }, []);

  useEffect(() => {
    let themeToSet: Theme;
    const preference = window.localStorage.getItem(themeLocalStorageKey);

    if (isValidTheme(preference)) {
      themeToSet = preference;
    } else {
      themeToSet = getBrowserPreference();
    }

    document.documentElement.setAttribute(
      "data-theme",
      themeToSet ?? DEFAULT_THEME,
    );
    setCurrentTheme(themeToSet);
  }, [setCurrentTheme]);

  useEffect(() => {
    window
      .matchMedia("(prefers-color-scheme: dark)")
      .addEventListener("change", () => {
        setCurrentTheme(getBrowserPreference());
      });
  }, [setCurrentTheme]);

  return (
    <ThemeContext.Provider value={{ setCurrentTheme, theme }}>
      {children}
    </ThemeContext.Provider>
  );
};
